import { _IdGenerator } from '@angular/cdk/a11y';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Input,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormBuilder, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { debounceTime, filter, startWith, Subject } from 'rxjs';

type BeltCircumference = {
  min: number;
  max: number;
};

@Component({
  imports: [MatInputModule, ReactiveFormsModule],
  selector: 'vectronic-formly-belt-circumference',
  template: ` <form [formGroup]="circumferenceControl">
    <input
      matInput
      type="number"
      formControlName="min"
      [id]="id"
      [min]="min()"
      [max]="maxFrom()"
      [placeholder]="focused ? 'Min' : ''"
      (focus)="_onFocus()"
      (blur)="_onBlur()"
    />

    <span class="separator">-</span>

    <input
      matInput
      type="number"
      formControlName="max"
      [min]="minTo()"
      [max]="maxTo()"
      (focus)="_onFocus()"
      (blur)="_onBlur()"
      [placeholder]="focused ? 'Max' : ''"
    />
  </form>`,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: BeltCircumferenceComponent,
    },
  ],
  styles: `
    form {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      gap: 1rem;
    }
    p {
      margin-bottom: 0.5rem;
      margin-left: 0.5rem;
    }
    span {
      margin-left: 0.5rem;
    }
    .separator {
      place-self: center;
    }
  `,
})
export class BeltCircumferenceComponent
  implements ControlValueAccessor, MatFormFieldControl<{ min: number; max: number }>, OnInit
{
  readonly fb = inject(FormBuilder);
  readonly _elementRef = inject(ElementRef);
  private readonly _idGenerator = inject(_IdGenerator);
  readonly ngControl = inject(NgControl, { self: true, optional: true })!;
  private destroyRef = inject(DestroyRef);

  // state
  circumferenceControl = this.fb.group({
    min: this.fb.control<number | null>(null),
    max: this.fb.control<number | null>(null),
  });
  private touched = signal(false);
  private status = signal<string>('VALID');
  private hasError = computed(() => {
    const invalid = this.status() === 'INVALID';
    const touched = this.touched();
    return invalid && touched;
  });
  // properties
  min = input.required<number>();
  maxRatio = input.required<number>();
  defaultMaxRatio = input<number>();
  circumferenceMin = toSignal(this.circumferenceControl.get('min').valueChanges);
  circumferenceMax = toSignal(this.circumferenceControl.get('max').valueChanges);
  maxFrom = computed(() => (this.circumferenceMax() ?? Infinity) - 1);
  minTo = computed(() => (this.circumferenceMin() ?? 0) + 1);
  maxTo = computed(() => Math.ceil((this.circumferenceMin() ?? Infinity) * this.maxRatio()));

  readonly stateChanges = new Subject<void>();
  onChange: (value: BeltCircumference) => void;
  onTouched: () => void;

  @Input()
  get id(): string {
    return this._id;
  }
  set id(value: string) {
    this._id = value || this._uid;
    this.stateChanges.next();
  }
  private _id!: string;
  private _uid = this._idGenerator.getId('mat-belt-circumference-');
  @Input() value: { min: number; max: number };
  @Input() placeholder: string;
  focused = false;
  @Input()
  required: boolean;
  disabled: boolean;
  errorState = true;
  controlType?: string;
  autofilled?: boolean;
  userAriaDescribedBy?: string;
  disableAutomaticLabeling?: boolean;

  constructor() {
    this.circumferenceControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((circumference) =>
        this.onChange?.({ min: circumference.min, max: circumference.max }),
      );

    this.circumferenceControl
      .get('min')
      .valueChanges.pipe(
        takeUntilDestroyed(),
        filter((value) => typeof value === 'number'),
        debounceTime(500),
      )
      .subscribe((min) => {
        if (!this.circumferenceControl.get('max').getRawValue() && this.defaultMaxRatio() != null) {
          this.circumferenceControl.get('max').setValue(Math.floor(min * this.defaultMaxRatio()));
        }
      });

    this.circumferenceControl
      .get('max')
      .valueChanges.pipe(
        takeUntilDestroyed(),
        filter((value) => typeof value === 'number'),
        debounceTime(500),
      )
      .subscribe((max) => {
        if (!this.circumferenceControl.get('min').getRawValue() && this.defaultMaxRatio() != null) {
          this.circumferenceControl.get('min').setValue(Math.ceil(max / this.defaultMaxRatio()));
        }
      });

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      const newState = this.hasError();
      if (newState !== this.errorState) {
        this.errorState = newState;
        this.stateChanges.next();
      }
    });
  }
  ngOnInit(): void {
    this.ngControl.statusChanges
      .pipe(startWith(this.ngControl.status), takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => this.status.set(status));
  }
  get empty() {
    const value = this.circumferenceControl.getRawValue();
    return value == null || (value.min == null && value.max == null);
  }
  get shouldLabelFloat() {
    return !this.empty || this.focused;
  }

  get describedByIds(): string[] {
    const element = this._elementRef.nativeElement;
    const existingDescribedBy = element.getAttribute('aria-describedby');

    return existingDescribedBy?.split(' ') || [];
  }

  setDescribedByIds(ids: string[]) {
    const element = this._elementRef.nativeElement;

    if (ids.length) {
      element.setAttribute('aria-describedby', ids.join(' '));
    } else {
      element.removeAttribute('aria-describedby');
    }
  }
  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() === 'input') {
      return;
    }

    this.focus();
  }
  focus(): void {
    const circumferenceValue = this.circumferenceControl.getRawValue();
    const minValue = circumferenceValue?.min;
    const maxValue = circumferenceValue?.max;
    if (!minValue || !!maxValue) {
      this._elementRef.nativeElement.querySelector('input[formControlName="min"]').focus();
    } else {
      this._elementRef.nativeElement.querySelector('input[formControlName="max"]').focus();
    }
  }

  writeValue(value: BeltCircumference): void {
    const circumference = { min: value?.min ?? null, max: value?.max ?? null };
    this.circumferenceControl.setValue(circumference, { emitEvent: false });

    this.circumferenceControl.markAsPristine();
    this.circumferenceControl.markAsUntouched();
    this.touched.set(false);
  }
  registerOnChange(fn: (value: BeltCircumference) => void): void {
    this.onChange = (value) => {
      fn(value);
      this.touched.set(true);
    };
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.touched.set(true);
    };
  }
  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.circumferenceControl.disable() : this.circumferenceControl.enable();
    this.disabled = isDisabled;
    this.stateChanges.next();
  }
  _onFocus() {
    if (!this.disabled) {
      this.focused = true;
      this.stateChanges.next();
    }
  }
  _onBlur() {
    this.focused = false;

    if (!this.disabled) {
      this.onTouched();
      this.stateChanges.next();
    }
  }
}
